package location

import kotlinx.browser.window
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.suspendCancellableCoroutine
import model.GeoPoint
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

class WebGeoLocation(
) : GeoLocation {

    private var watchId: Int? = null

    private val _updates = MutableSharedFlow<GeoPoint>(
        replay = 1,
        extraBufferCapacity = 1
    )

    override val updates: Flow<GeoPoint>
        get() = _updates

    // The browser starts its timeout after the user answers the permission prompt
    private val positionOptions: dynamic = run {
        val options: dynamic = js("({})")
        options.enableHighAccuracy = true
        options.timeout = LOCATION_FIX_TIMEOUT.inWholeMilliseconds.toInt()
        options.maximumAge = 0
        options
    }

    override suspend fun current(): GeoPoint {
        return suspendCancellableCoroutine { continuation ->

            val geolocation = window.navigator.asDynamic().geolocation

            if (geolocation == null) {
                continuation.resumeWithException(GeoError.NotSupported)
                return@suspendCancellableCoroutine
            }

            geolocation.getCurrentPosition(
                { position ->
                    val coords = position.coords

                    continuation.resume(
                        GeoPoint(
                            lat = coords.latitude as Double,
                            lon = coords.longitude as Double
                        )
                    )
                },
                { error ->
                    continuation.resumeWithException((error.code as Int).toGeoError())
                },
                positionOptions
            )
        }
    }

    override suspend fun start() {
        val geolocation = window.navigator.asDynamic().geolocation ?: return

        if (watchId != null) return

        watchId = geolocation.watchPosition(
            { position ->
                val coords = position.coords

                _updates.tryEmit(
                    GeoPoint(
                        lat = coords.latitude as Double,
                        lon = coords.longitude as Double
                    )
                )
            },
            // Watch errors are ignored; current() is what reports failures to the screen
            { _ -> },
            positionOptions
        ) as Int
    }

    override fun stop() {
        val id = watchId ?: return
        watchId = null
        val geolocation = window.navigator.asDynamic().geolocation ?: return

        geolocation.clearWatch(id)
    }

    // GeolocationPositionError codes; browsers report a blocked site and a dismissed prompt the same way
    private fun Int.toGeoError(): GeoError = when (this) {
        1 -> GeoError.PermissionDenied
        2 -> GeoError.Unavailable
        3 -> GeoError.Timeout
        else -> GeoError.Unknown(Exception("Geolocation error code $this"))
    }
}
